import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import shallowEqual from 'shallowequal';
import _ from 'underscore';

import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { Filter } from '~commonInfra/utils/Filter';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { selectCurrentlyApplicableContentTypes, selectMixedContentFilters, selectProjectSpaceContentTypes, selectSpecificMixedContentFilterClause } from '~src/spacesPlatform/selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';

import { BackgroundJobTaskType } from '../../../backgroundJob/types/BackgroundJobInfo';
import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../codegen/Localize';
import { contentCountsUpToDate } from '../../actions/ContentCountsActions';
import { ContentTypeFilterSelectedAction, filterContentType } from '../../actions/ContentExplorerFilterActions';
import { ContentFilterAppliedAction, filterMixedContent, mixedContentFilterApplied, SetContentFilterNoRequestContentAction, UpdateFiltersInBothUrlAndStateAction, updateFiltersInBothUrlAndStateAction } from '../../actions/filters/FilterActions';
import { doesActionCreateDifferentFilter, doesActionCreateSameFilter } from '../../actions/filters/FilterHelpers';
import { requestSpaceContents } from '../../actions/RequestSpaceContentsActions';
import { ContentFiltersKeys } from '../../models/ContentFilters';
import { ExplorableItemType } from '../../models/ContentType';
import { FilterFieldNames, IStringValuedFilterClause, IValuedFieldFilterClause } from '../../models/IFilterClause';
import { OrderKeys } from '../../models/OrderKeys';
import { FilterMap } from '../../state/ContentExplorerState';
import { isFavoritesSpace, isProjectSpace, isSiteUserContentSubspace,isSomeKindaSearchSpace } from '../../state/SpaceInfo';
import { updateOrderQueryParamsIfMismatched } from './SortContentSagas';

function isStayingOnMixedContent(contentTypes: ReadonlyArray<ExplorableItemType>, previousContentTypes: ReadonlyArray<ExplorableItemType>): boolean {
  return previousContentTypes && previousContentTypes.length > 1 && contentTypes.length > 1;
}

export function* filterByContentType(action: ContentTypeFilterSelectedAction): SagaIterator {
  try {
    // using selectContentTypeFilterValues instead of selectCurrentlyApplicableContentTypes here
    // because selectCurrentlyApplicableContentTypes returns defaults if content types are null
    const previousContentTypes = yield select(selectProjectSpaceContentTypes);
    const spaceInfo = yield select(selectSpaceInfo);

    // only send out the state update if there is something different or we've got null types.
    // This prevents the ContentCounts from incorrectly setting counts-up-to-date to false when we
    // haven't actually updated the content types due to logic in ContentTypeReducer
    if (!previousContentTypes || !shallowEqual(previousContentTypes, action.contentTypes)) {

      yield put(filterContentType(action.contentTypes));

      // if we are moving from mixed content to mixed content
      // We know the counts can't change so avoid spinners and making count requests
      if (!action.clearAndSet && (isProjectSpace(spaceInfo) || isFavoritesSpace(spaceInfo) || isSiteUserContentSubspace(spaceInfo))
        && isStayingOnMixedContent(action.contentTypes, previousContentTypes)) {
          yield put(contentCountsUpToDate());
      }
    }

    updateOrderQueryParamsIfMismatched(action.contentTypes, spaceInfo);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* applyMixedContentFilter(action: ContentFilterAppliedAction): SagaIterator {
  try {
    const currentFilter: IStringValuedFilterClause = yield select(selectSpecificMixedContentFilterClause, action.filterType);
    if (doesActionCreateDifferentFilter(currentFilter, action)) {
      yield call(handleInterdependentFilters, action);
      yield put(filterMixedContent(action.filterType, action.value));
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* setContentFilterNoRequestContent(action: SetContentFilterNoRequestContentAction): SagaIterator {
  try {
    const currentFilter: IStringValuedFilterClause = yield select(selectSpecificMixedContentFilterClause, action.filterType);
    if (doesActionCreateDifferentFilter(currentFilter, action)) {
      yield put(filterMixedContent(action.filterType, action.value));
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}


/**
 * Handles cases in which other filter actions need to be dispatched when a
 * filter changes.
 *
 * For example: A user may decide to filter exclusively on system jobs using a
 * job type dropdown but the system jobs are hidden due to the "show system
 * jobs" checkbox being unticked. In that case this method ticks that checkbox.
 *
 * @param action The ContentFilterAppliedAction to handle interdependent filters
 *  for. Does not apply the filter in the action.
 */
export function* handleInterdependentFilters(action: ContentFilterAppliedAction): SagaIterator {
  const filters: FilterMap = yield select(selectMixedContentFilters);
  const filter = filters[action.filterType] as IValuedFieldFilterClause<any>;
  const isNoopAction = !_.isUndefined(filter) && doesActionCreateSameFilter(filter, action)
    || _.isUndefined(filter) && action.value === undefined;
  if (isNoopAction) {
    return; // Do nothing
  }
  const isDelete = !_.isUndefined(filter) && action.value === undefined;
  switch (action.filterType) {
    case 'showSystemJobs':
      const onlySystemJobsFilter = Filter.stringFieldEq(
        FilterFieldNames.taskType, BackgroundJobTaskType.System);
      const isOnlyShowingSystemJobs = shallowEqual(filters.backgroundJobTaskType, onlySystemJobsFilter);
      // If we are no longer showing system jobs then we cannot be filtering exclusively on them
      if (isDelete && isOnlyShowingSystemJobs) {
        yield put(updateFiltersInBothUrlAndStateAction(ContentFiltersKeys.backgroundJobTaskType, undefined));
      }
      break;
    case 'backgroundJobTaskType':
      const filteringExclusivelyOnSystemJobs = action.value === BackgroundJobTaskType.System;
      // If we are filtering exclusively on system jobs we need to show them
      if (filteringExclusivelyOnSystemJobs) {
        yield put(updateFiltersInBothUrlAndStateAction(ContentFiltersKeys.showSystemJobs, true));
      }
      break;
  }
}

/**
 * Sets the url to match the new filter value, as well as dispatching an action
 * to update the redux store with the new filter value
 * @param action The UpdateFiltersInBothUrlAndStateAction to update the URL and
 * set the redux filter state for
 */
export function* updateFiltersInBothUrlAndState(action: UpdateFiltersInBothUrlAndStateAction): SagaIterator {
  // This function isn't automatically called whenever a filter is modified because
  // very often the filter modification happens _because_ the URL was modified

  // Instead we call it only when we know we need to modify the redux filter state
  const search: any = WindowLocationService.getHashQueryObject();
  const value: string | undefined = _.isUndefined(action.value) ? undefined : String(action.value);
  if(search[action.filterType] === value) {
    return; // No-op
  }
  WindowLocationService.updateHashQueryParameterWithReplace(action.filterType, value, {encode: false});
  yield put(mixedContentFilterApplied(action.filterType, value));
}

// Borrowed from FilterPanelCtrl::clearAllFilters
export function clearMixedContentFilters() {
  // Remove each of the filter keys.
  const search: any = WindowLocationService.getHashQueryObject();
  const inSearchSpace = isSomeKindaSearchSpace(selectSpaceInfo(getAppStore().getState()));

  _.each(_.keys(search), (key: string) => {
    if (_.has(ContentFiltersKeys, key)) {
      if (inSearchSpace && key === ContentFiltersKeys.search) {
        // don't remove the search filter in the search space.
        return;
      }
      WindowLocationService.deleteQueryParameter(key);
    }
  });

  // removeRelevanceFromOrder when showing only views/workbooks/datasources
  const contentTypes = selectCurrentlyApplicableContentTypes(getAppStore().getState());
  if (contentTypes.length === 1 && (contentTypes[0] === 'datasource'
      || contentTypes[0] === 'workbook'
      || contentTypes[0] === 'view')) {
    const search: any = WindowLocationService.getHashQueryObject();
    const orderValue = search[UrlParamKeys.Order];
    if (orderValue) {
      // The user may have added other order params, so we only remove relevance.
      let order: ReadonlyArray<IOrderClause> = Order.fromInput(orderValue);
      order = Order.withoutField(order, OrderKeys.relevance);
      const serializedOrder = Order.serialize(order);
      // we want to remove the search query parameter if there is none, passing 'null' does that.
      if (!serializedOrder) {
        WindowLocationService.deleteQueryParameter(UrlParamKeys.Order);
      } else {
        WindowLocationService.updateHashQueryParameterWithReplace(UrlParamKeys.Order, serializedOrder, {encode: false});
      }
    }
  }
}
